import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import { Grid, Paper } from "@mui/material";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { getSmartkioskInitialValues } from "app/pages/customers/dsok/utils/getSmartkioskInitialValues";
import SmartkioskSettingsFields, {
  SETTINGS_FIELD_SCHEMA,
} from "app/pages/customers/dsok/smartkioskFields/SmartkioskSettingsFields";
import { DSOK_CUSTOMER_FIELD_SCHEMA } from "app/pages/customers/dsok/smartkioskFields/SmartkioskCustomerFields";
import DsokCustomerDetailLayout from "../components/DsokCustomerDetailLayout";
import DsokCustomerDetailDirtySync from "../components/DsokCustomerDetailDirtySync";
import DsokCustomerDetailSectionRegister from "../components/DsokCustomerDetailSectionRegister";
import { useDsokCustomerDetail } from "../data/useDsokCustomerDetail";
import { sanitizeLogoUrlString } from "app/pages/customers/dsok/utils/sanitizeLogoUrlString";

const SETTINGS_FIELD_NAME = "SETTINGS_FIELD";

const DsokCustomerAppSettingsPage = () => {
  const { t } = useTranslation();
  const auth = useJumboAuth();
  const authUser = auth?.getAuthUser?.() || {};
  const userTyp = authUser?.type ?? authUser?.typ ?? 1;
  const isAdmin = userTyp === 1;

  const { customer } = useDsokCustomerDetail();
  const [expanded, setExpanded] = useState(SETTINGS_FIELD_NAME);
  const handleChange = (panel) => (_event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const formInitialValues = useMemo(() => {
    const fallback = {
      customer: {
        ...DSOK_CUSTOMER_FIELD_SCHEMA,
        auth: [],
        paymentMethods: [],
      },
      settings: { ...SETTINGS_FIELD_SCHEMA },
    };
    if (!customer) return fallback;
    return getSmartkioskInitialValues(customer) ?? fallback;
  }, [customer]);

  return (
    <DsokCustomerDetailLayout
      lastBreadcrumbLabel={t("dsokCustomerDetail.sections.appSettings.title")}
    >
      <Paper
        variant="outlined"
        sx={{
          bgcolor: "background.paper",
          p: 2,
          borderRadius: 1,
        }}
      >
        <Formik
          initialValues={formInitialValues}
          enableReinitialize
          onSubmit={() => {}}
        >
          <Form>
            <DsokCustomerDetailDirtySync />
            <DsokCustomerDetailSectionRegister
              sectionId="app-settings"
              getPayload={(values) => ({
                ...(values.settings ?? {}),
                _customerMainLogo:
                  typeof values.customer?.mainLogo === "string"
                    ? sanitizeLogoUrlString(values.customer.mainLogo)
                    : values.customer?.mainLogo,
                _customerAuth: values.customer?.auth,
                _customerSecondaryAuth: values.customer?.secondaryAuth,
                _customerPaymentMethods: values.customer?.paymentMethods,
              })}
            />
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SmartkioskSettingsFields
                  handleChange={handleChange}
                  expanded={expanded}
                  isAdmin={isAdmin}
                  hideSectionTitle
                />
              </Grid>
            </Grid>
          </Form>
        </Formik>
      </Paper>
    </DsokCustomerDetailLayout>
  );
};

export default DsokCustomerAppSettingsPage;
